import { BUSINESSES, BUSINESS_KEYS } from "@/consts/businesses";
import { OpeningsJson } from "./openingsClient";
import type { OpeningsBusiness } from "./openingsTypes";

/**
 * Stable identifiers for the Openings businesses this app talks to. Use these
 * keys throughout the Openings adapters instead of hardcoding handles so a
 * single registry controls which business each call targets (multi-business
 * support for the Eddie's Edition + Edition Studio sibling brands).
 *
 * Re-exported from `@/consts/businesses`, the single source of truth — see
 * that module for the registry itself.
 */
export type { EditionBusinessKey } from "@/consts/businesses";
export {
  DEFAULT_EDITION_BUSINESS,
  isEditionBusinessKey,
  toEditionBusinessKey,
} from "@/consts/businesses";

import type { EditionBusinessKey } from "@/consts/businesses";
import { DEFAULT_EDITION_BUSINESS } from "@/consts/businesses";

/** Maps a business key to its Openings handle (slug). Derived from BUSINESSES. */
export const EDITION_BUSINESS_HANDLES = Object.fromEntries(
  BUSINESS_KEYS.map((key) => [key, BUSINESSES[key].handle]),
) as Record<EditionBusinessKey, string>;

/**
 * Resolve the Openings business record for the given key. This is the single
 * entry point every Openings adapter should use to look up a business.
 */
export const resolveOpeningsBusiness = (
  business: EditionBusinessKey = DEFAULT_EDITION_BUSINESS,
): Promise<OpeningsBusiness> =>
  OpeningsJson.getBusinessBySlug(EDITION_BUSINESS_HANDLES[business]);

/** This business's welcome-coupon Openings offer id + Bearer secret. */
export type WelcomeOfferConfig = {
  offerId: string;
  secret: string;
};

/**
 * Resolve `business`'s welcome-coupon offer id + Bearer secret from
 * `process.env`, read at the point of call (never cached at module load) so
 * every call site — including tests — sees the current environment with no
 * import-order dependency.
 *
 * The env var names are derived from each business's `offerEnvSuffix` in the
 * `BUSINESSES` registry (`OPENINGS_OFFER_ID_<suffix>` /
 * `OPENINGS_OFFER_SECRET_<suffix>`) — two businesses, two independent
 * Openings offers, two independent secrets — a business's secret authorizes
 * only its OWN offer's codes (mint AND revoke both scope by offer id in the
 * path; see `openingsClient.ts`'s `mintWelcomeCode`/`revokeWelcomeCode`).
 *
 * Returns `null`, and NEVER throws, when either var is missing or
 * blank/whitespace-only: the welcome-coupon feature can launch before the
 * salon's Openings offers are configured in the dashboard, and "not
 * configured" is a normal, expected state, not a fault. Callers decide what
 * that means for them — `OpeningsJson.mintWelcomeCode` /
 * `revokeWelcomeCode` turn it into a distinguishable `NOT_CONFIGURED` error,
 * and the GET route past them (Task 5) turns THAT into `state: "none"`.
 */
export const resolveWelcomeOfferConfig = (
  business: EditionBusinessKey,
): WelcomeOfferConfig | null => {
  const suffix = BUSINESSES[business].offerEnvSuffix;
  const offerId = process.env[`OPENINGS_OFFER_ID_${suffix}`]?.trim();
  const secret = process.env[`OPENINGS_OFFER_SECRET_${suffix}`]?.trim();
  if (!offerId || !secret) return null;
  return { offerId, secret };
};
